/**
 * Resolve a user-facing label for a semantic field.
 *
 * Priority:
 *   1. Per-visual field override (if provided via fieldOverrides[name].displayName)
 *   2. field.display_name (preferred — curated short name)
 *   3. field.description  (legacy fallback)
 *   4. Title-cased field name (final fallback)
 *
 * Defensively rejects values that look like long descriptions ending in '.' or
 * longer than 60 chars, so mis-filled display_names don't bleed into chart
 * legends and axes.
 */
const _LOOKS_LIKE_DESCRIPTION = (s) => {
  if (!s || typeof s !== 'string') return false;
  const t = s.trim();
  if (t.length > 60) return true;
  if (t.endsWith('.')) return true;
  return false;
};

// Proper Title Case: lowercase everything, then capitalise each word start so
// "LOSS PAID DATE" / "loss_paid_date" both render as "Loss Paid Date".
const _titleize = (s) =>
  String(s).replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());

// Curated labels may be stored in ALL CAPS, which reads as shouting in the UI.
// Title-case a label ONLY when it has letters but no lowercase letter, so
// intentionally mixed-case names (e.g. "Loss Ratio", "YoY %") stay untouched.
const _fix = (s) => {
  const t = String(s);
  return /[a-z]/.test(t) ? t : _titleize(t);
};

export function getFieldLabel(field, fieldName, fieldOverride) {
  const fallbackName = fieldName;
  if (fieldOverride && fieldOverride.displayName) return fieldOverride.displayName;
  if (field) {
    // business_name is the curated, canonical short name; display_name/label
    // are legacy fallbacks. Physical raw names (e.g. an aggregate binding's
    // display_name === its column name) are rejected only implicitly — a real
    // curated name always wins because it's checked first.
    if (field.business_name && !_LOOKS_LIKE_DESCRIPTION(field.business_name)) return _fix(field.business_name);
    if (field.display_name && !_LOOKS_LIKE_DESCRIPTION(field.display_name)) return _fix(field.display_name);
    if (field.label && !_LOOKS_LIKE_DESCRIPTION(field.label)) return _fix(field.label);
    // Legacy: some existing models only have `description` populated. Use it as
    // a label ONLY when the field's own name is a raw physical column (snake /
    // ALL_CAPS). If the name is already human-friendly (e.g. "Loss Ratio"), the
    // name is a better label than a definition-like description.
    if (field.description && !_LOOKS_LIKE_DESCRIPTION(field.description)) {
      const nm = fallbackName || field.name || '';
      const nameIsFriendly = /[a-z]/.test(nm) && /\s/.test(nm) && !/_/.test(nm);
      if (!nameIsFriendly) return field.description;
    }
  }
  // Strip any internal entity-disambiguation qualifier ("ent_ab12cd34::FIELD")
  // so the raw technical reference never leaks into a user-facing label.
  const base = String(fallbackName || (field && field.name) || '').replace(/^[A-Za-z0-9_]+::/, '');
  return _titleize(base);
}

// Build an entity-qualified label ("Claimant · Full Legal Name") for a field
// whose bare name collides across entities. Prefers the curated LOGICAL entity
// name over the raw physical table name (so "DIM_PARTY" never leaks), and
// collapses the prefix entirely when it would merely repeat the field label
// (e.g. entity "Insured" + field "Insured" → just "Insured").
export function entityQualifiedLabel(entityName, entityLogicalName, baseLabel) {
  const raw = entityLogicalName || entityName || '';
  const ent = _fix(String(raw).replace(/_/g, ' ').trim());
  const base = String(baseLabel);
  if (!ent) return base;
  if (ent.toLowerCase() === base.trim().toLowerCase()) return base;
  return `${ent} · ${base}`;
}

export function resolveLabel(allFields, name, fieldOverrides) {
  const fields = allFields || [];
  const f = fields.find((ff) => ff && ff.name === name);
  const ov = fieldOverrides && fieldOverrides[name];
  if (f || ov) return getFieldLabel(f, name, ov);
  // Entity-qualified key ("<entity_id>::FIELD") with no exact field match — never
  // leak the raw id to a header. Resolve the entity's friendly name (from any
  // field of that entity) + the base field's label.
  if (typeof name === 'string' && name.includes('::')) {
    const idx = name.indexOf('::');
    const entId = name.slice(0, idx);
    const fieldPart = name.slice(idx + 2);
    const baseF = fields.find((ff) => ff && (ff._baseName === fieldPart || ff.name === fieldPart));
    const baseLabel = getFieldLabel(baseF, fieldPart, fieldOverrides && fieldOverrides[fieldPart]);
    const entField = fields.find((ff) => ff && (ff.entity_id === entId || ff.entity_name === entId));
    return entField ? entityQualifiedLabel(entField.entity_name, entField.entity_logical_name, baseLabel) : baseLabel;
  }
  return getFieldLabel(f, name, ov);
}
